import { useMemo } from 'react';
import _ from 'lodash';
import { useLocation } from 'react-router-dom';

import FilterRegistry from '../components/page-framework/filter/FilterRegistry';

export const filterStateParam = 'filterState';

/*
 * Private functions.
 */

function escapeEncodedFilterState(encodedFilterState) {
    // Replace any instances of the filter delimiter so they don't confuse the parameter parser.  This just
    // replaces the delimiter with a double delimiter, since the parser will only recognize a single delimiter
    // character as an actual delimiter.
    return encodedFilterState.replace(/\|{1}/g, '||');
}

function unescapeEncodedFilterState(escapedEncodedFilterState) {
    // Undoes the escaping process described in the _escapeEncodedFilterState function.  See that function for more
    // information.
    return escapedEncodedFilterState.replace(/\|{2}/g, '|');
}

/*
 * Exported functions.
 */

export function buildUrlParamValue(encodedFilterState) {
    return _(encodedFilterState)
        .omitBy((encodedState) => encodedState === undefined)
        .map((encodedState, filterId) => `${filterId}:${escapeEncodedFilterState(encodedState)}`)
        .join('|');
}

export function parseUrlParamValue(urlParam) {
    // This regular expression is complex.  Here's an explanation of each piece:
    // (?:^|\|) Ensures that the param name is preceded by either the start of the string or a delimiter character
    // ([^:]*?) Captures the parameter name (any character that is not a : character)
    // ((?:[^|]|\|{2})*?) Captures the parameter's encoded value (any character that is not a single delimiter character)
    // (?:\|{1}[^|]|$)) Ensures the parameter's encoded value is followed by a single delimiter or the end of the string
    // The last two pieces are necessary because a parameter's encoded value can contain escaped delimiters, which are
    // escaped by replacing the delimiter with two delimiters.  These pieces ensure only a single delimiter can
    // delineate the encoded state values.
    const paramRegex = new RegExp('(?:^|\\||)([^:]*?):((?:[^|]|\\|{2})*?)(?:\\|(?!\\|)|$)', 'g');
    let match = paramRegex.exec(urlParam);
    const encodedFilterState = {};

    while (match) {
        const filterId = match[1];
        encodedFilterState[filterId] = unescapeEncodedFilterState(match[2]);
        match = paramRegex.exec(urlParam);
    }

    return encodedFilterState;
}

export function getEncodedStateFromUrl(search) {
    const urlParams = new URLSearchParams(search || window.location.search);
    if (urlParams.has(filterStateParam)) {
        return parseUrlParamValue(urlParams.get(filterStateParam));
    }

    return undefined;
}

export function getUrlParamFromEncodedState(encodedFilterState, currentParams = '') {
    const urlParams = new URLSearchParams(currentParams);

    if (!_.isEmpty(encodedFilterState)) {
        urlParams.set(filterStateParam, buildUrlParamValue(encodedFilterState));
    }

    return urlParams.toString();
}

export function setUrlFromEncodedState(encodedFilterState) {
    const path = window.location.pathname;
    const urlParams = getUrlParamFromEncodedState(encodedFilterState, window.location.search);
    const newQuery = _.isEmpty(urlParams) ? '' : `?${urlParams}`;
    window.history.replaceState(null, '', `${path}${newQuery}`);
}

export function encodeState(filterState) {
    return (
        _(filterState)
            // Map the filter IDs to the primary IDs.  This ensures any legacy filter IDs are mapped
            // to the current ID (i.e. ServiceDeliveryStatus -> Status).
            .mapKeys((value, key) => FilterRegistry.getPrimaryFilterId(key))
            .mapValues((state, filterId) => {
                const filterStateService = FilterRegistry.getFilterStateService(filterId);
                return filterStateService.encode(state);
            })
            .value()
    );
}

export function useUrlState() {
    const location = useLocation();
    const urlState = useMemo(() => getEncodedStateFromUrl(location.search), [location]);

    return [urlState, setUrlFromEncodedState];
}
